import React, {useContext, useState} from 'react';
import {
    Modal, Box, SpaceBetween, Button, FormField, Input, Textarea, Select, FileDropzone, FileInput, FileTokenGroup, Alert
} from '@amzn/awsui-components-react';
import {
    INITIAL_STATE, getSegmentTypeOptions, UploadSheetActionTypes, uploadSheetFormReducer, validateState
} from '../reducer/UploadMDASheetReducer';
import {
    MultidimensionalAllocationContext
} from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import TPENavigator from 'src/components/TPENavigator';
import CONSTANTS from 'src/utils/constants';

interface UploadSheetFormModalProps {
    visible: boolean;
    onHide: () => void;
}

export default function UploadMDASheetModal({visible, onHide}: UploadSheetFormModalProps) {
    const [state, dispatch] = useReducerWithLogger(uploadSheetFormReducer, INITIAL_STATE);
    const [isSubmitting, setSubmitting] = useState(false);
    const [submitErrors, setSubmitErrors] = useState<string | undefined>(undefined);
    const [navigateURL, setNavigateURL] = useState('');

    const {services} = useContext(MultidimensionalAllocationContext);
    const service = services.multidimensionalAllocationService;
    // LG Code segment option availability is gated by the backend GetCOATypes LOV,
    // which the backend filters on the Secondary Ledger feature flag (fail closed).
    const [backendCoaTypes] = services.dataSourcesService.getCoaTypes('upload-mda-sheet-modal');
    const segmentTypeOptions = getSegmentTypeOptions(backendCoaTypes || []);


    const handleNameChange = (value: string) => {
        dispatch({type: UploadSheetActionTypes.SET_NAME, name: value});
    };

    const handleDescriptionChange = (value: string) => {
        dispatch({type: UploadSheetActionTypes.SET_DESCRIPTION, description: value});
    };

    // Handle segment type change
    const handleSegmentTypeChange = (selectedOption: any) => {
        dispatch({
            type: UploadSheetActionTypes.SET_SEGMENT_TYPE,
            segmentType: selectedOption.detail.selectedOption.value,
            segmentTypeOptions
        });
    };

    // Handle file selection
    const handleFileChange = (files: File[]) => {
        dispatch({type: UploadSheetActionTypes.SET_ALLOCATION_SHEETS, allocationSheets: files});
    };

    // Handle file dismiss
    const handleFileDismiss = (fileIndex: number) => {
        const updatedFiles = state.allocationSheets.filter((_, index) => index !== fileIndex);
        dispatch({type: UploadSheetActionTypes.SET_ALLOCATION_SHEETS, allocationSheets: updatedFiles});
    };

    const handleSubmit = async () => {
        /* Submission has three steps:
            - form data validations
            - upload the file to s3. 
            - create the allocationSheet in ATP (with metadata from the uploaded s3 file).
        */
    
        setSubmitErrors(undefined); // clear all previous errors

        const errors = validateState(state, segmentTypeOptions);
        if (Object.values(errors).some((err) => err != undefined)) {
            dispatch({type: UploadSheetActionTypes.SET_ERRORS, errors: errors})
            return;
        }

        try {            
            setSubmitting(true);
            const s3_key = `${state.name.replace(/\s+/g, '_')}_${Date.now()}` // sample: SheetName_213214215132
            await service.uploadMultiAllocationSheet(s3_key, state.allocationSheets[0])
            
            // Call the backend create API after successful S3 upload
            const createAllocationSheetPayload = {
                sheetName: state.name,
                segmentType: state.segmentType,
                description: state.description,
                fileUrl: s3_key
            };
            
            const response = await service.createMultiAllocationSheet(createAllocationSheetPayload);
            
            // Navigate to the created sheet details page
            const sheetId = response.sheetData.sheetId;
            setNavigateURL(`${CONSTANTS.PAGE_NAV.MULTIDIMENSIONAL_ALLOCATION_SHEET_DETAILS.URL}/${sheetId}`);
            
            setSubmitErrors(undefined);
            onHide();
        } catch (err) {
            const errMsg = err instanceof Error ? `${err.message}` : `${err}`
            setSubmitErrors(errMsg);
            return;
        } finally {
            setSubmitting(false)
        }
    };

    // Handle cancel
    const handleCancel = () => {
        dispatch({type: UploadSheetActionTypes.RESET_FORM});
        setSubmitErrors(undefined);
        onHide();
    };

    return (<>
        <TPENavigator path={navigateURL} />
        <Modal
        onDismiss={(event) => {
            event.detail.reason !== 'overlay' && handleCancel()
        }}
        visible={visible}
        closeAriaLabel="Close modal"
        size="medium"
        footer={<Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    Submit for review
                </Button>
            </SpaceBetween>
        </Box>}
        header="Create multidimensional allocation sheet"
    >
        <SpaceBetween direction="vertical" size="m">
            {"Do you want to upload a multidimensional allocation worksheet? " +
                "Your file will be submitted for review before the allocations are applied."}

            {/* Name Field */}
            <FormField
                label="Name"
                errorText={state.errors.name}
            >
                <Input
                    value={state.name}
                    onChange={({detail}) => handleNameChange(detail.value)}
                    placeholder="Enter name without special characters"
                />
            </FormField>

            {/* Description Field */}
            <FormField
                label="Description"
                errorText={state.errors.description}
            >
                <Textarea
                    value={state.description}
                    onChange={({detail}) => handleDescriptionChange(detail.value)}
                    placeholder="Enter description"
                    rows={3}
                />
            </FormField>

            {/* Segment Type Field */}
            <FormField
                label="Segment to allocate on"
                errorText={state.errors.segmentType}
            >
                <Select
                    selectedOption={segmentTypeOptions.find((segment) => segment.value === state.segmentType)!}
                    onChange={handleSegmentTypeChange}
                    options={segmentTypeOptions}
                    placeholder="Select segment type"
                />
            </FormField>

            {/* File Upload Field */}
            <FormField errorText={state.errors.allocationSheets}>
                <FileDropzone
                    onChange={({detail}) => handleFileChange(detail.value)}
                >
                    <SpaceBetween size="xxs" alignItems="center">
                        <Box color="inherit">
                            Drag and drop files, or select from below.
                        </Box>
                        <FileInput
                            value={state.allocationSheets}
                            onChange={({detail}) => handleFileChange(detail.value)}
                            multiple={true}
                            accept=".xlsx,.xls"
                        >
                            Add allocation sheet
                        </FileInput>
                    </SpaceBetween>
                </FileDropzone>

                {state.allocationSheets.length > 0 && (<Box margin={{top: 's'}}>
                    <FileTokenGroup
                        items={state.allocationSheets.map(file => ({file}))}
                        onDismiss={({detail}) => handleFileDismiss(detail.fileIndex)}
                        i18nStrings={{
                            limitShowFewer: "Show fewer files",
                            limitShowMore: "Show more files",
                            errorIconAriaLabel: "Error",
                            warningIconAriaLabel: "Warning"
                        }}
                    />
                </Box>)}
            </FormField>
            {!!submitErrors && <Alert type="error" dismissible header="Submit failed."> {submitErrors} </Alert>}

        </SpaceBetween>
    </Modal>
    </>);
}
